import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
  Divider,
  Tooltip,
  Snackbar,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import moment from "moment";
import { getData } from "../../../services/library";
import { UrlApi } from "../../../services/apirest";
import AddNotes from "./AddNotes";
import DeleteNotes from "./DeleteNotes";

const NotasModal = ({ open, onClose, candidatoId, candidatoNombre }) => {
  const [notas, setNotas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [notaParaEditar, setNotaParaEditar] = useState(null);
  const [loadingNotas, setLoadingNotas] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notaParaEliminar, setNotaParaEliminar] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const abrirModal = (nota = null) => {
    setNotaParaEditar(nota);
    setIsEditMode(!!nota);
    setModalOpen(true);
  };

  const user = localStorage.getItem("user") || "Anónimo";

  const handleSaveNota = async ({ candidatoId, contenido, user, noteId }) => {
    const method = noteId ? "PUT" : "POST";
    const url = noteId
      ? `${UrlApi}/solicitudDePersonal/notas/${noteId}`
      : `${UrlApi}/solicitudDePersonal/notas`;

    const payload = {
      id_candidato: candidatoId,
      contenido,
      user,
    };

    try {
      const response = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Error al guardar");

      obtenerNotas();
      showSnackbar(
        noteId ? "Nota actualizada con éxito" : "Nota agregada con éxito"
      );
    } catch (error) {
      console.error("Error guardando nota:", error);
      showSnackbar("Error al guardar la nota", "error");
    }
  };

  const handleDeleteNota = async (noteId) => {
    try {
      const response = await fetch(
        `${UrlApi}/solicitudDePersonal/notas/${noteId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        return;
      }
      showSnackbar(`Nota con ID ${noteId} eliminada con éxito`);
      setNotaParaEliminar(null);
      obtenerNotas();
    } catch (error) {
      showSnackbar(`Error al eliminar la nota con el ID ${noteId}`, "error");
      console.error("Error al eliminar nota:", error);
    }
  };

  const obtenerNotas = async () => {
    setLoadingNotas(true);
    try {
      getData(`/solicitudDePersonal/notas/${candidatoId}`, (response) => {
        const datos = Array.isArray(response.data) ? response.data : [];
        setNotas(datos);
        setLoadingNotas(false);
      });
    } catch (error) {
      console.error("Error obteniendo notas:", error);
      showSnackbar("Error al obtener las notas", "error");
      setLoadingNotas(false);
    }
  };

  useEffect(() => {
    if (open) obtenerNotas();
  }, [open, candidatoId]);

  return (
    <>
      <AddNotes
        open={modalOpen}
        onClose={() => {
          setIsEditMode(false);
          setNotaParaEditar(null);
          setModalOpen(false);
        }}
        candidatoId={candidatoId}
        onSave={handleSaveNota}
        isEditMode={isEditMode}
        contenidoInfo={notaParaEditar ? notaParaEditar.contenido : ""}
        noteId={notaParaEditar ? notaParaEditar.id : null}
      />
      <DeleteNotes
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setNotaParaEliminar(null);
        }}
        handleDelete={async () => {
          await handleDeleteNota(notaParaEliminar?.id);
          setDeleteModalOpen(false);
          setNotaParaEliminar(null);
        }}
      />

      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Notas del Candidato: {candidatoNombre}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              sx={{ mb: 2 }}
              onClick={() => abrirModal()}
            >
              Agregar Nueva Nota
            </Button>
            <Divider />
          </Box>

          {loadingNotas ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : notas.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#888" }}>
              No hay notas registradas para este candidato
            </Typography>
          ) : (
            <Stack spacing={2}>
              {notas.map((nota) => (
                <Box
                  key={nota.id}
                  sx={{
                    borderLeft: "4px solid #3498db",
                    p: 2,
                    backgroundColor: "#fafafa",
                    position: "relative",
                  }}
                >
                  {user === nota.user && (
                    <>
                      <IconButton
                        onClick={() => abrirModal(nota)}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 40,
                          color: "#3498db",
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          color: "#e74c3c",
                        }}
                        onClick={() => {
                          setNotaParaEliminar(nota); // ✅ guarda la nota actual
                          setDeleteModalOpen(true); // ✅ abre el modal de confirmación
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}

                  <Typography sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
                    {nota.contenido}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      color: "#666",
                      fontSize: "0.85rem",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <PersonIcon fontSize="small" />
                      <Typography>{nota.nombreApellido}</Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AccessTimeIcon fontSize="small" />
                      <Typography>
                        {moment(nota.fecha_actualizacion).format(
                          "DD/MM/YYYY HH:mm"
                        )}
                      </Typography>
                    </Box>
                    {nota.fecha_actualizacion !== nota.fecha_creacion && (
                      <Tooltip
                        title={`Fecha creacion: ${moment(
                          nota.fecha_creacion
                        ).format("DD/MM/YYYY HH:mm")}`}
                      >
                        <Typography sx={{ fontStyle: "italic" }}>
                          (editado)
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotasModal;
